class WaccPdpBuyBox extends HTMLElement {
  connectedCallback() {
    this.product = this.parseProduct();
    this.form = this.querySelector('[data-wacc-pdp-product-form]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.price = this.querySelector('[data-price]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.addText = this.querySelector('[data-add-to-cart-text]');
    this.optionGroups = Array.from(this.querySelectorAll('.wacc-pdp-buy__option-group'));
    this.bindOptions();
    this.bindQuantity();
  }

  parseProduct() {
    const script = this.querySelector('[data-product-json]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('WACC PDP: product JSON could not be parsed', error);
      return null;
    }
  }

  bindOptions() {
    this.optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          // Never hard-block navigation. Selecting a value always resolves to
          // the best in-stock variant for that value (auto-switching the other
          // options if the exact combo is sold out), so the picker can never
          // dead-end. If nothing is in stock for the value we still select it,
          // and the Add-to-Cart button shows "Sold Out".
          this.selectOption(groupIndex, button.dataset.optionValue);
        });
      });
    });
  }

  selectOption(groupIndex, value) {
    if (!this.product) return;
    const variants = this.product.variants || [];
    const desired = this.selectedOptions();
    desired[groupIndex] = value;
    const exact = variants.find((v) => v.options.every((o, i) => o === desired[i]));
    const target =
      (exact && exact.available && exact) ||
      variants.find((v) => v.options[groupIndex] === value && v.available) ||
      exact ||
      variants.find((v) => v.options[groupIndex] === value);
    if (!target) return;
    this.syncActive(target);
    this.applyVariant(target);
  }

  syncActive(variant) {
    this.optionGroups.forEach((group, gi) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.optionValue === variant.options[gi]);
      });
    });
  }

  bindQuantity() {
    const input = this.querySelector('.wacc-pdp-buy__quantity-input');
    this.querySelectorAll('.wacc-pdp-buy__quantity-button').forEach((button) => {
      button.addEventListener('click', () => {
        if (!input) return;
        const current = parseInt(input.value, 10) || 1;
        const next = button.name === 'plus' ? current + 1 : Math.max(1, current - 1);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  selectedOptions() {
    return this.optionGroups.map((group) => {
      const selected = group.querySelector('.wacc-pdp-buy__option.is-active');
      return selected ? selected.dataset.optionValue : '';
    });
  }

  updateVariant() {
    if (!this.product) return;
    const selected = this.selectedOptions();
    const variant = this.product.variants.find((candidate) =>
      candidate.options.every((option, index) => option === selected[index])
    );
    if (!variant) return;
    this.applyVariant(variant);
  }

  applyVariant(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this.dispatchEvent(new CustomEvent('cre43:variant-change', { bubbles: true, detail: { variant } }));
    if (this.price) this.price.textContent = this.formatMoney(variant.price);

    if (this.addButton && this.addText) {
      this.addButton.disabled = !variant.available;
      this.addText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    this.updateOptionAvailability();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateOptionAvailability() {
    if (!this.product) return;
    const selected = this.selectedOptions();
    this.optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const test = [...selected];
        test[groupIndex] = button.dataset.optionValue;
        const variant = this.product.variants.find((candidate) =>
          candidate.options.every((option, index) => option === test[index])
        );
        // Mark visually as unavailable but keep it clickable (prevents the
        // dead-end where a greyed option could not be re-selected).
        button.classList.toggle('is-unavailable', !variant || !variant.available);
        button.disabled = false;
      });
    });
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(cents / 100);
  }
}

if (!customElements.get('wacc-pdp-buy-box')) {
  customElements.define('wacc-pdp-buy-box', WaccPdpBuyBox);
}
